import { appPageHref, sectionOf } from "../../shared/appPages.ts";

/**
 * Registered pages: a name → the CHUNK that owns it, not the loader itself (2026-08-25).
 *
 * ⚠ **The registry is STATIC and the module behind it is not**, and keeping those two apart is the
 * whole design. Every page used to be a static import in `app.ts`, so the first paint of the app
 * waited on the code of eighteen pages the reader was not looking at - `/compose` alone shipped
 * 261KB of source it never ran. A `() => import(...)` thunk here defers the bytes; the KEYS stay
 * synchronous, which is what the three "is this a real page?" checks in `app.ts` need (a boot that
 * had to await a chunk before deciding whether an address is known would flash the dashboard at
 * every deep link).
 *
 * ⚠ **A thunk must be a bare `() => import("…")` with a literal path.** esbuild's splitting reads
 * these statically; a computed specifier (`import(`./pages/${name}.ts`)`) bundles the whole
 * directory into one chunk again and the gain silently disappears.
 */
export const pageLoaders: Record<string, () => Promise<() => void>> = {};

/**
 * The pages whose chunk has ARRIVED, keyed by name - which is a different question from which pages
 * exist, and the one `reloadPage` has to ask. A cross-surface refresh ("I just accepted a pitch, so
 * the dashboard's numbers are stale") fires at pages the reader may never have opened, and fetching
 * a chunk to re-render a page that has never rendered is work with no reader: the page builds itself
 * from fresh data the moment it IS opened. So an unloaded page is a no-op here, deliberately.
 */
const loadedPages: Record<string, () => void> = {};

/**
 * Which navigation is current. ⚠ **A chunk fetch opens a window in which the reader can navigate
 * again**, and two things go wrong without a sequence number: the second page's `pendingSubPath`
 * would be consumed by the first page's loader when it finally arrives (`/releases/12` landing on
 * the settings page's idea of a sub-path), and the first page would then render itself into a shell
 * whose active section is somebody else's. A stale loader is simply dropped.
 */
let navigationSeq = 0;

/**
 * Fetch a page's chunk (or find it already here) and run its loader, unless the reader has moved on.
 * Returns once the page has had its turn, so a caller that wants to know the page is up can await.
 */
async function runPage(page: string, seq: number): Promise<void> {
	const already = loadedPages[page];
	if (already) {
		already();
		return;
	}
	const chunk = pageLoaders[page];
	if (!chunk) return;
	const load = await chunk();
	loadedPages[page] = load;
	// ⚠ Checked AFTER the await, never before: the point of the number is the window the await opens.
	if (seq === navigationSeq) load();
}

/**
 * Start ONE page's chunk now, without running its loader (2026-08-25, second pass).
 *
 * ⚠ **The chunk of the page the address names was not requested until 301ms, and nothing about it
 * was slow** - it was queued behind boot's three serial rounds of `/api`:
 *
 * ```
 * 135 ms  the shared chunk               (app.js has parsed)
 * 198 ms  /api/meta, theme, a11y         <- boot awaits
 * 245 ms  /api/ai/status, /api/settings/mail  <- boot awaits these too
 * 301 ms  /chunks/dashboard-*.js         <- only now do we ask for the page
 * ```
 *
 * ⚠ **The generalizable finding: a fetch that depends on NOTHING must not inherit the ordering of
 * the code that happens to sit above it.** Which chunk to fetch is answerable from `location` in the
 * first tick, and every `await` between there and `navigateTo()` was buying the answer to a
 * different question. Firing it at the top of boot costs a line and removes those rounds from the
 * critical path entirely; `runPage` finds the module already resolved (the browser dedupes a repeat
 * `import()` of the same specifier) and the navigation is unchanged.
 *
 * ⚠ **A guess, and it must stay a cheap one.** A signed-out tenant sees the login screen instead, and
 * a first-run owner is redirected to the guide - so this can fetch a chunk nobody opens. That is the
 * right trade only while page chunks are small; it would not be if one page ever became the bundle
 * again.
 */
export function warmPage(page: string): void {
	const chunk = pageLoaders[page];
	if (!chunk || loadedPages[page]) return;
	void chunk()
		.then(load => (loadedPages[page] ??= load))
		.catch(() => undefined);
}

/** How long the LCP candidates must stay quiet before the paint counts as settled. */
const LCP_SETTLE_MS = 2000;
/** ...and the deadline for a browser that reports no LCP at all. See the observer's `catch`. */
const LCP_FALLBACK_MS = 5000;

/**
 * Warm every page's chunk once the reader's own page has painted (2026-08-25).
 *
 * Splitting buys a fast FIRST paint and pays for it with a fetch on the first visit to each page,
 * which is the wrong trade for an app somebody works in all day: they navigate constantly and they
 * arrive once. So the bytes still all get downloaded - just AFTER the first render, off the critical
 * path, at the browser's idle convenience. By the time a nav item is clicked the chunk is in the
 * HTTP cache and the navigation is as synchronous as it ever was.
 *
 * ⚠ Deliberately not awaited and deliberately silent: a warm-up that fails costs nothing, because
 * the real navigation will fetch again and report properly.
 *
 * ⚠ **It waits for the LCP to SETTLE, and `load` was not late enough** (2026-08-25, second pass).
 * An idle callback alone came first and was wrong: on a throttled phone the main thread is quiet for
 * long stretches *while the network is still bringing in the things the first paint needs*, so the
 * warm-up competed for bandwidth with the page the reader is looking at. `load` was the fix, and the
 * next Lighthouse run said it is still too early - by 300ms:
 *
 * ```
 * observedLoad   199 ms   <- the warm-up arms here
 * chunks start   301 ms   <- 425 KB of pages nobody asked for
 * observedLCP    507 ms   <- and the largest element has still not painted
 * ```
 *
 * ⚠ **The reusable half: `load` is a fact about the DOCUMENT'S OWN SUBRESOURCES, and this app's
 * first paint is not one of them.** Every byte the dashboard needs - its chunk, the dictionary, three
 * rounds of `/api` - is fetched by script AFTER `load` has fired, so on a fast connection `load` lands
 * while the app is still deciding what to draw. **When you want "the reader can see their page", ask
 * the paint, never the loader.**
 *
 * So the trigger is the LCP itself, through a `PerformanceObserver`. Each new largest-element
 * candidate pushes the timer out; when the candidates stop arriving for `LCP_SETTLE_MS` the paint has
 * settled and the bandwidth is genuinely spare. ⚠ **The fallback timer is not belt-and-braces, it is
 * the whole answer in two browsers** - Safari and Firefox ship no `largest-contentful-paint` entry
 * type at all, and an observer that throws on `observe()` would otherwise leave them never warming.
 */
export function prefetchPages(): void {
	const warm = () => {
		for (const [page, chunk] of Object.entries(pageLoaders)) {
			if (loadedPages[page]) continue;
			void chunk()
				.then(load => (loadedPages[page] ??= load))
				.catch(() => undefined);
		}
	};
	const idle = () => {
		if ("requestIdleCallback" in window) requestIdleCallback(warm, { timeout: 4000 });
		else setTimeout(warm, 1500);
	};
	let timer: ReturnType<typeof setTimeout> = setTimeout(idle, LCP_FALLBACK_MS);
	try {
		const observer = new PerformanceObserver(() => {
			clearTimeout(timer);
			timer = setTimeout(() => {
				observer.disconnect();
				idle();
			}, LCP_SETTLE_MS);
		});
		// `buffered` because this runs at the END of boot, long after the first candidate was
		// recorded - without it the observer would hear nothing on a page whose LCP has already
		// happened, and the fallback timer would be doing all the work on every browser.
		observer.observe({ type: "largest-contentful-paint", buffered: true });
	} catch {
		// No LCP entry type here (Safari, Firefox). The fallback timer above already stands.
	}
}

/**
 * Sub-path segments of the last navigation (`/releases/12/link` → ["12", "link"]),
 * handed to the page loader exactly once via takeSubPath(). Null when the loader
 * runs outside a navigation (reloadPage refreshes) - pages keep their state then.
 */
let pendingSubPath: string[] | null = null;

/** Consume the navigation's sub-path (once) - null for non-navigation reloads */
export function takeSubPath(): string[] | null {
	const sub = pendingSubPath;
	pendingSubPath = null;
	return sub;
}

/**
 * Called after every navigation, whoever started it (round 160).
 *
 * The one thing this module knows that nobody else does is that a navigation HAPPENED - the click
 * handlers in app.ts see only their own links, and popstate sees only the back button, while pages
 * deep-link into each other all day (the attention strip's own rows do). Chrome that has to stay
 * current across the whole app subscribes here rather than trying to enumerate the ways a page can
 * change. Deliberately a plain listener list: the router itself fetches nothing and knows nothing
 * about what a listener is for.
 */
const navigationListeners: (() => void)[] = [];

export function onNavigate(listener: () => void): void {
	navigationListeners.push(listener);
}

/** The one reserved query parameter: which dialog is open. See `syncDialog` below for the design. */
const DIALOG_PARAM = "d";

/**
 * The address bar, as this app writes it.
 *
 * ⚠ **REAL PATHS since 2026-08-10** (owner ask: *"we use hash navigation right now, but I think we
 * should use the history API instead, so we get real path navigation without hashes"*). The move is
 * shell-wide rather than a router change, and the three parts that made it one are worth naming:
 * every `href="#…"` in the app, a SERVER catch-all that must not shadow `/api`, `/signin`, the
 * self-hosted published pages at `/a/<artist>/…`, `/privacy`, `/terms` or `/data-deletion` (it
 * already existed and already sits below all of them), and the fact that **the old addresses are not
 * all ours** - `#settings` is stored on Stripe's side as the billing portal's return_url. That last
 * one is why `pathFromLegacyHash` exists and why the boot translates rather than ignores.
 *
 * ⚠ **The query string is PRESERVED and the hash is DROPPED, and the asymmetry is deliberate.** The
 * checkout comes back to `/?billing=success#settings`: the query is a message to the app that has
 * not been read yet at the moment the first navigation happens, and the hash is the address, which
 * this navigation is replacing. Dropping the query here would have silently deleted the toast that
 * tells somebody their payment went through.
 */
function routeUrl(page: string, subPath: (string | number)[], dropDialog = false): string {
	const params = new URLSearchParams(location.search);
	// ⚠ A NAVIGATION drops the open dialog's key; a page syncing its own state keeps it. A dialog
	// belongs to the page it was opened on, so carrying `?d=edit-12` onto another page leaves a stray
	// parameter nothing there reads - and carrying it through the sidebar back to the same page
	// reopens a dialog the reader just navigated away from. A TAB change is the other case and must
	// keep it: the editor is open OVER the tab, and dropping it there would close it on every click.
	if (dropDialog) params.delete(DIALOG_PARAM);
	const query = params.toString();
	return appPageHref(page, subPath) + (query === "" ? "" : `?${query}`);
}

/**
 * Light the sidebar row of the page's JOB and the page's own tab (§AL round 3). `data-section` is on
 * both the sidebar row and the section's tab bar, so one toggle shows the right bar and highlights the
 * right row; `aria-current` says which tab is the page, which a class never told a screen reader.
 *
 * ⚠ **No `!` on the lookups any more.** The old line asserted a `[data-page]` for every page, and a
 * page whose nav row an edition had removed - Discover in saas - threw on arrival, so the dashboard's
 * own "find outlets" button crashed the router on the hosted product. A missing tab now simply marks
 * nothing, which is the right answer for a page the edition does not offer a tab to.
 */
function markNavigation(page: string): void {
	const section = sectionOf(page);
	document.querySelectorAll("[data-page]").forEach(link => {
		const current = link.getAttribute("data-page") === page;
		link.classList.toggle("active", current);
		if (current) link.setAttribute("aria-current", "page");
		else link.removeAttribute("aria-current");
	});
	document.querySelectorAll("[data-section]").forEach(item => item.classList.toggle("active", item.getAttribute("data-section") === section));
	// On a phone the bar scrolls inside itself (six Outreach tabs are wider than 390px), and arriving on
	// Rejected by a link would leave the tab that says where you are off the right edge. Centred by
	// scrollLeft rather than scrollIntoView, which would also scroll the PAGE to reach the bar.
	const bar = document.querySelector<HTMLElement>(`.section-tabs[data-section="${section}"]`);
	const tab = bar?.querySelector<HTMLElement>(`[data-page="${page}"]`);
	if (bar != null && tab != null && bar.scrollWidth > bar.clientWidth) bar.scrollLeft = tab.offsetLeft - (bar.clientWidth - tab.offsetWidth) / 2;
}

/**
 * @param fromHistory the browser ALREADY set the address (a Back or Forward press), so this call must
 * not touch it. ⚠ **Without the flag the forward half of a dialog's history is destroyed**: Forward
 * onto `?d=edit-12` would arrive with the key set, `navigateTo` would compute a URL without it (a
 * navigation drops a dialog, see `routeUrl`) and push that over the top - so Forward appeared to do
 * nothing. Closing, cancelling and going Back are three verbs and only one of them writes the URL;
 * `artists.ts`'s `#closeEditor` carries the same distinction one layer up.
 */
export function navigateTo(page: string, subPath: string[] = [], fromHistory = false) {
	document.querySelectorAll(".page").forEach(p => p.classList.remove("active"));
	document.getElementById(`page-${page}`)!.classList.add("active");
	markNavigation(page);
	const url = routeUrl(page, subPath, true);
	// ⚠ Compared against pathname + search, NOT against `location.href`: an origin in the comparison
	// makes it never match, and every navigation would push a duplicate history entry - which is a
	// Back button that needs two presses, the classic symptom of exactly this line.
	if (!fromHistory && location.pathname + location.search !== url) {
		history.pushState(null, "", url);
	}
	pendingSubPath = subPath;
	// ⚠ The SHELL switches synchronously above and the page's own code may arrive a moment later -
	// the section is on screen, empty, while its chunk lands. That order is the right one: showing
	// the destination immediately and filling it reads as loading, whereas holding the old page
	// still until the chunk arrives reads as a click that did nothing. `prefetchPages()` makes the
	// gap disappear on every navigation after the first render anyway.
	void runPage(page, ++navigationSeq);
	for (const listener of navigationListeners) listener();
}

/**
 * Reflect in-page state (open workspace, subnav tab) in the URL so a refresh lands
 * back on the same spot. No-op unless `page` is the one on screen - background
 * refreshes of other pages must not rewrite the address bar. `push` records a
 * history entry (opening/closing a workspace); the default replace keeps tab
 * switches from flooding history.
 *
 * ⚠ **Still called `syncHash` after the paths landed, and that is a lie worth NOT telling** - it is
 * `syncRoute` now. Sixteen call sites across three islands, renamed in the same edit, because a
 * function whose name describes the mechanism it stopped using is how the next reader concludes this
 * app still routes on the hash.
 */
export function syncRoute(page: string, subPath: (string | number)[] = [], push = false) {
	if (!document.getElementById(`page-${page}`)?.classList.contains("active")) return;
	const url = routeUrl(page, subPath);
	if (location.pathname + location.search === url) return;
	if (push) history.pushState(null, "", url);
	else history.replaceState(null, "", url);
}

/**
 * A DIALOG'S ADDRESS (owner ask, 2026-08-11, the moment paths landed: *"make it possible to link to
 * dialogs, so that they are URL-state driven - clicking Edit on an artist should append something to
 * the URL, so clicking the back button closes the dialog, and opening the page on that URL opens said
 * dialog"*).
 *
 * ⚠ **A QUERY PARAMETER, not a path segment, and that is the decision the whole design rests on.** A
 * dialog is a LAYER over a page that already has state - the artist editor opens from the roster AND
 * from inside a workspace that is showing a tab, and `/artists/12/bio` has no room for a third
 * meaning. A path grammar would need a per-page answer to "where does the dialog go" and every page
 * would invent a different one. One reserved param composes with whatever the page's own sub-path
 * already says, and it reads honestly in the bar: `/artists/12/bio?d=edit-12`.
 *
 * ⚠ **Opening PUSHES and closing REPLACES.** Back must close the dialog - that is the ask - so the
 * open has to be its own history entry. But closing by button then pushing a second entry would mean
 * Back REOPENS it, which is nobody's idea of Back. Replace on close and the two gestures agree.
 *
 * ⚠ **`d` is a short opaque key the PAGE owns**, deliberately not a structured `?dialog=artist&id=12`:
 * the router does not know what a dialog is, and a page reconciling its own key against its own state
 * is the only version where a page can decide that a row it cannot find means "close" rather than
 * "open something empty".
 */
/**
 * The dialog key in the address right now, or null. Read it in `load()` and reconcile.
 *
 * ⚠ **It takes the PAGE and answers null for any page not on screen, which is the same guard
 * `syncDialog` carries and was missing here until round 248.** The asymmetry looked harmless while
 * one page had a vocabulary: `reloadPage("outlets")` runs the outlets loader from the SENT page's own
 * triage handler, and a loader that reconciles reads whatever key happens to be in the bar - which
 * belongs to whichever dialog the reader actually has open somewhere else. **The keys are not
 * namespaced and cannot be**: `edit-3` is a perfectly good key on three different pages now, so an
 * unguarded read has a hidden page fetching record 3 of its own kind, opening a modal nobody can
 * see, and toasting about an outlet at somebody editing an artist. The writing half never had this
 * bug because it was guarded on the day it was written; **a read and a write of the same state need
 * the same guard, and only one of them announces itself when it is missing.**
 */
export function currentDialog(page: string): string | null {
	if (!document.getElementById(`page-${page}`)?.classList.contains("active")) return null;
	return new URLSearchParams(location.search).get(DIALOG_PARAM);
}

/**
 * Put a dialog in the address, or take it out. No-op unless `page` is the one on screen - the same
 * guard `syncRoute` has, and for the same reason: a background refresh of another page must not
 * rewrite the bar.
 */
export function syncDialog(page: string, key: string | null): void {
	if (!document.getElementById(`page-${page}`)?.classList.contains("active")) return;
	const params = new URLSearchParams(location.search);
	if (key == null) params.delete(DIALOG_PARAM);
	else params.set(DIALOG_PARAM, key);
	const query = params.toString();
	const url = location.pathname + (query === "" ? "" : `?${query}`);
	if (location.pathname + location.search === url) return;
	if (key == null) history.replaceState(null, "", url);
	else history.pushState(null, "", url);
}

/**
 * Reload a specific page (used for cross-page refreshes).
 *
 * ⚠ **A page whose chunk has never loaded is a no-op**, not a fetch - see `loadedPages`. The caller
 * is saying "the data behind that page changed"; a page that has not rendered has no stale copy to
 * correct, and it will read the new data when somebody opens it.
 */
export function reloadPage(name: string) {
	loadedPages[name]?.();
}
